import { useEffect, useRef, useState } from "react";
import styles from "./contactlists.module.css";
import { Link } from "react-router-dom";
import Helmet from "react-helmet";
import Footer from "../../components/Footer";
import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Slide, Snackbar, TablePagination, TextField, Tooltip, Typography } from "@mui/material";
import { ArrowLeft, Home, MoreVertical, Plus, Search, Trash2, X } from "react-feather";
import { Dropdown } from "react-bootstrap";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import Loader from "../../components/Loader";
import { TableLoaderSmall } from "../../components/LoaderC";
import UserAdd from "./components/AddForm";
import { toast } from 'react-smart-toaster';
import PeoplePageTitle from "../tenants/components/PeoplePageTitle";
import useDebouncedValue from "../../hooks/useDebouncedValue";

const SEARCH_DEBOUNCE_MS = 400;

const ContactList = () => {
  const { getAccessTokenSilently, isAuthenticated, user } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [allData, setAllData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(8);
  const [reloadKey, setReloadKey] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [filterData, setFilterData] = useState({ searchKey: "" });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editUserData, setEditUserData] = useState(null);
  const [selectecId, setSelectecId] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [groupFilterText, setGroupFilterText] = useState("None");
  const [groupFilterData, setGroupFilterData] = useState([]);
  const [sendMailOpen, setSendMailOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const latestRequest = useRef(0);

  const debouncedSearch = useDebouncedValue(filterData.searchKey, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    const requestId = ++latestRequest.current;

    const getContactList = async () => {
      try {
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        const response = await axios.get(process.env.REACT_APP_USER_API_URL + "v1/contact-lists/paginated", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            page: page + 1,
            per_page: perPage,
            search: debouncedSearch.trim() || undefined,
          },
        });

        // Ignore responses from requests that were superseded by newer searches/pages
        if (requestId !== latestRequest.current) return;

        const rows = response.data?.data || [];
        const total = response.data?.meta?.total || 0;

        // Deleting the only contact on the last page empties it, so step back to the new last page
        if (rows.length === 0 && page > 0 && total > 0) {
          setPage(Math.ceil(total / perPage) - 1);
          return;
        }

        setAllData(rows);
        setTotalCount(total);
        setLoadError(false);
      } catch (error) {
        if (requestId !== latestRequest.current) return;
        console.error("[Contacts] Failed to load contact list", error);
        setLoadError(true);
        toast.error(error?.response?.data?.message || "Unable to load contacts. Please try again.");
      } finally {
        if (requestId === latestRequest.current)
          setContentLoading(false);
      }
    };

    getContactList();
  }, [getAccessTokenSilently, page, perPage, debouncedSearch, reloadKey]);

  // New contacts land on the first page, so reload from the server instead of prepending locally
  const contactAddLocal = () => {
    setPage(0);
    setReloadKey((prev) => prev + 1);
  };

  useEffect(() => {
    let selectedData = allData?.map(item => {
      if (groupFilterData.indexOf(item.group_name) > -1) {
        return item.id;
      }
      return null;
    }).filter(i => i !== null);

    setSelectedIds(selectedData);
  }, [allData, groupFilterData, groupFilterText]);

  const confirmDelete = async () => {
    setDeleteModalOpen(false);
    setLoading(true);
    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    axios.delete(process.env.REACT_APP_USER_API_URL + 'v1/contact-lists/' + selectecId, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).then(response => {
      setSnackbarOpen(true);
      setSnackbarMsg(response.data.message);
      setLoading(false);

      // Reload the page so the next contact moves up and the total stays accurate
      setReloadKey((prev) => prev + 1);
      setSelectecId(0);
    }).catch(error => {
      if (typeof error.response !== 'undefined')
        toast.error(error.response.data.message);
      setLoading(false);
    });
  }

  const confirmSend = async () => {
    if (selectedTemplate === '') {
      toast.error("Please select email template.");
      return false;
    }

    setLoading(true);
    setSendMailOpen(false);
    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    let data = { data: JSON.stringify(selectedIds), template: selectedTemplate }

    axios.post(process.env.REACT_APP_USER_API_URL + 'v1/send-contact-mail', data, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).then(response => {
      setSnackbarOpen(true);
      setSnackbarMsg(response.data.message);
      setLoading(false);

      setSelectedIds([]);

      setGroupFilterText("None");
      setGroupFilterData([]);
    }).catch(error => {
      if (typeof error.response !== 'undefined')
        toast.error(error.response.data.message);
      setLoading(false);
    });
  }

  return (<>
    {loading && <Loader />}
    <Helmet title="Contact List" />

    <div className={`${styles.pageTitleWrap}`}>
      <PeoplePageTitle defaultPage={'Contacts'} />
      <Typography display="inline" className={`${styles.pageTitle} ${styles.Big}`}>Contacts</Typography>
      <nav aria-label="breadcrumb">
        <ol className={`${styles.breadcrumb}`}>
          <li className={`${styles.breadcrumbItem}`}><span><Home /></span>People</li>
          <li className={`${styles.breadcrumbItem} ${styles.active}`}>Contacts</li>
        </ol>
      </nav>
    </div>

    <div className={`${styles.BodyCon}`}>
      <div className={`${styles.mainCardDiv}`}>
        <div className={`${styles.mainCard}`}>
          {contentLoading && <div className="Loader TableLoader"><TableLoaderSmall /></div>}

          {!contentLoading && <div className="control-pane">
            <div className={`${styles.Row}`}>

              <div className={`${styles.SearchFilterSec}`}>
                <div className={`${styles.SearchCon}`}>
                  <div className={`${styles.SearchConSec}`}>
                    <Search />
                    <input type="text" placeholder="Search" value={filterData.searchKey} onChange={(e) => { setFilterData(prev => { return { ...prev, searchKey: e.target.value } }); setPage(0); }} />
                  </div>
                </div>
                {isAuthenticated && user["https://propertydek.com/permissions"].indexOf("emailcontact:create") > -1 && <div className={`${styles.FilterSec}`}>
                  <Link className={`${styles.AddNoteBu}`} onClick={(e) => { setAddModalOpen(true) }}>
                    <Plus /> <span>Contact</span>
                  </Link>
                </div>}
              </div>

              <div className={`${styles.NotesRow}`}>
                {allData.map((item) => {
                  return <div key={item?.id} className={`${styles.ProspectCard}`} style={{ cursor: 'pointer' }} onClick={(e) => {
                    if (isAuthenticated && user["https://propertydek.com/permissions"].indexOf("emailcontact:create") > -1) {
                      setEditModalOpen(true);
                      setEditUserData(item);
                    } else {
                      return false;
                    }
                  }}>
                    <div className={`${styles.ProspectCardUnder}`}>
                      <div className={`${styles.ProspectCardHeadSection}`}>
                        {isAuthenticated && user["https://propertydek.com/permissions"].indexOf("emailcontact:delete") > -1 && <Dropdown className={`${styles.ActionLeaseBUSec}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                          <Dropdown.Toggle className={`${styles.ActionsLeaseBU} ActionsBU`} id="dropdown-basic">
                            <MoreVertical />
                          </Dropdown.Toggle>

                          <Dropdown.Menu className={`${styles.ActionsBUList} ActionsBUList`}>
                            <Dropdown.Item className={`${styles.EditActionsBUListItem} ${styles.Delete}`} onClick={(e) => { setDeleteModalOpen(true); setSelectecId(item.id); }}>
                              <Trash2 /> Delete
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>}

                        <div className={`${styles.ProsIMGSec}`}>
                          <img src="/static/img/contact-list.svg" alt="" className={`${styles.Icon}`} />
                        </div>
                        <div className={`${styles.HeadConSec}`}>
                          <p className={`${styles.ProsUserName}`}>
                            <span className={`${styles.UserName}`}>{item?.name}</span>
                          </p>
                          <p className={`${styles.ProsUserName} ${styles.Small}`}>{item?.group_name}</p>
                        </div>
                      </div>
                      <div className={`${styles.ProspectCardBodySec}`}>
                        <ul>
                          <li>
                            <p className={`${styles.Title}`}>Email:</p>
                            <p className={`${styles.Content}`}>{item?.email}{!item?.email && ('-')}</p>
                          </li>
                          <li>
                            <p className={`${styles.Title}`}>Phone:</p>
                            <p className={`${styles.Content}`}>{item?.phone}{!item?.phone && ('-')}</p>
                          </li>
                          <li>
                            <p className={`${styles.Title}`}>Company:</p>
                            <p className={`${styles.Content}`}>{item?.company_name}{!item?.company_name && ('-')}</p>
                          </li>
                          <li>
                            <p className={`${styles.Title}`}>Interest:</p>
                            <p className={`${styles.Content} ${styles.Limit}`}>
                              {item?.interest_arr.length > 0 && item?.interest_arr[0]}
                              {item?.interest_arr.length === 0 && ('-')}
                            </p>
                            {item?.interest_arr.length > 1 && <Tooltip title={item?.interest_arr?.join(', ')}><span className={`${styles.IntToolBU}`}>+{item?.interest_arr?.length - 1}</span></Tooltip>}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                })}

                {allData.length > 0 && (<div className="Pagination TablePaginationNew List">
                  <TablePagination
                    count={totalCount}
                    color="primary"
                    page={page}
                    rowsPerPage={perPage}
                    onPageChange={(e, v) => setPage(v)}
                    rowsPerPageOptions={[
                      { label: "4", value: 4 },
                      { label: "8", value: 8 },
                      { label: "16", value: 16 },
                      { label: "50", value: 50 },
                    ]}
                    onRowsPerPageChange={(e) => {
                      setPage(0);
                      setPerPage(Number(e.target.value));
                    }}
                    labelRowsPerPage={"Contacts per Page:"}
                  />
                </div>)}

                {allData.length === 0 && loadError && (<div className={`${styles.NoDataMain}`}>
                  <p className={`${styles.NoDataText}`}>Something went wrong</p>
                  <p className={`${styles.NoDataTextSub}`}>We couldn't load contacts. Please refresh the page to try again.</p>
                </div>)}

                {allData.length === 0 && !loadError && (<div className={`${styles.NoDataMain}`}>
                  <div className={`${styles.NoDataIMG}`}>
                    <img src="/static/img/no-prospect.png" alt="No Data" />
                  </div>
                  <p className={`${styles.NoDataText}`}>Let's get started!</p>
                  <p className={`${styles.NoDataTextSub}`}>You don't have any contacts saved.</p>
                </div>)}

              </div>

            </div>
          </div>}
        </div>
      </div>
    </div>

    <Dialog
      open={deleteModalOpen}
      onClose={(e) => setDeleteModalOpen(false)}
      fullWidth
      maxWidth="sm"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp"
    >
      <DialogTitle id="alert-dialog-title">Would you like to delete this contact?<button onClick={(e) => setDeleteModalOpen(false)}><X /></button></DialogTitle>
      <DialogContent>
        <div className="ModalFormGroup">
          <label className="PopupBodyText">The contact information will be removed immediately.You can't undo this action.</label>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={(e) => confirmDelete()} className="CancelPopupBU">Delete</Button>
        <Button onClick={(e) => setDeleteModalOpen(false)} className="SubmitPopupBU">Cancel</Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={addModalOpen}
      onClose={(e) => { setAddModalOpen(false) }}
      fullWidth
      maxWidth="sm"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp MobileForm"
    >
      <DialogTitle id="alert-dialog-title">
        <button className={`${styles.BackAddBU}`} onClick={(e) => { setAddModalOpen(false) }}><ArrowLeft /></button>
        Add Contact
        <button onClick={(e) => { setAddModalOpen(false) }}><X /></button>
      </DialogTitle>
      <DialogContent>
        <UserAdd setAddModalOpen={setAddModalOpen.bind(this)} setLoading={setLoading.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMsg={setSnackbarMsg.bind(this)} setAllData={contactAddLocal} editData={null} />
      </DialogContent>
    </Dialog>

    <Dialog
      open={editModalOpen}
      onClose={(e) => { setEditModalOpen(false) }}
      fullWidth
      maxWidth="sm"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp MobileForm"
    >
      <DialogTitle id="alert-dialog-title">
        <button className={`${styles.BackAddBU}`} onClick={(e) => { setEditModalOpen(false) }}><ArrowLeft /></button>
        Update Contact
        <button onClick={(e) => { setEditModalOpen(false) }}><X /></button>
      </DialogTitle>
      <DialogContent>
        <UserAdd setAddModalOpen={setEditModalOpen.bind(this)} setLoading={setLoading.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMsg={setSnackbarMsg.bind(this)} setAllData={setAllData.bind(this)} editData={editUserData} />
      </DialogContent>
    </Dialog>

    <Dialog
      open={sendMailOpen}
      onClose={(e) => setSendMailOpen(false)}
      fullWidth
      maxWidth="xs"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp"
    >
      <DialogTitle id="alert-dialog-title">Please select email template.<button onClick={(e) => setSendMailOpen(false)}><X /></button></DialogTitle>
      <DialogContent>
        <div className="ModalFormGroup">
          <div className={`${styles.HalfSecFull} FormGroup`}>
            <Autocomplete
              disableClearable
              openOnFocus
              options={['DLD Thanks Email']}
              renderInput={(params) => <TextField {...params} label="Email Template" variant="outlined" />}
              value={selectedTemplate}
              onChange={(event, newValue) => {
                setSelectedTemplate(newValue);
              }}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={(e) => setSendMailOpen(false)} className="CancelPopupBU">Cancel</Button>
        <Button onClick={(e) => confirmSend()} className="SubmitPopupBU">Send</Button>
      </DialogActions>
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      onClose={(e) => setSnackbarOpen(false)}
      TransitionComponent={Slide}
      message={snackbarMsg}
      autoHideDuration={10000}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      action={
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={(e) => setSnackbarOpen(false)}
        >
          <X />
        </IconButton>
      }
    />
    <Footer />
  </>)
}

export default ContactList;